<template>
  <div class="lang-switcher" ref="containerRef">
    <!-- Trigger Button -->
    <button
      class="lang-switcher__button"
      :class="{ 'lang-switcher__button--active': isOpen }"
      @click="toggleDropdown"
    >
      <LanguageSquareIcon class="navbar__icon" />
      <span class="lang-code">{{ languages[activeIndex].code }}</span>
      <ArrowDown01Icon class="arrow-icon" :class="{ 'arrow-icon--rotated': isOpen }" />
    </button>

    <!-- Dropdown Menu -->
    <div class="lang-dropdown" ref="dropdownRef" v-show="isOpen">
      <div
        v-for="(lang, index) in languages"
        :key="lang.code"
        class="lang-dropdown__item"
        :class="{ 'lang-dropdown__item--active': activeIndex === index }"
        @click="(e) => handleItemClick(index, e)"
      >
        <span class="item-label">{{ lang.label }}</span>
        <span class="item-code">{{ lang.code }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { LanguageSquareIcon, ArrowDown01Icon } from 'hugeicons-vue'
import gsap from 'gsap'

const { locale } = useI18n()

// Config
const languages = [
  { code: 'ES', label: 'Español' },
  { code: 'EN', label: 'English' },
  { code: 'PT', label: 'Português' },
]

// Initialize activeIndex based on current locale
const getInitialIndex = () => {
  const currentCode = locale.value.toUpperCase()
  const index = languages.findIndex((lang) => lang.code === currentCode)
  return index !== -1 ? index : 0
}

const activeIndex = ref(getInitialIndex())
const isOpen = ref(false)
const containerRef = ref(null)
const dropdownRef = ref(null)
let sparkContainer = null

// Watch for external locale changes
watch(locale, (newLocale) => {
  const code = newLocale.toUpperCase()
  const index = languages.findIndex((lang) => lang.code === code)
  if (index !== -1) {
    activeIndex.value = index
  }
})

const toggleDropdown = () => {
  if (isOpen.value) {
    closeDropdown()
  } else {
    openDropdown()
  }
}

const openDropdown = () => {
  isOpen.value = true

  // Kill any running tweens
  gsap.killTweensOf(dropdownRef.value)
  gsap.killTweensOf('.lang-dropdown__item')

  const tl = gsap.timeline()

  // Menu Animation
  tl.fromTo(
    dropdownRef.value,
    {
      y: 10,
      opacity: 0,
      scale: 0.95,
      transformOrigin: 'bottom right',
    },
    {
      y: 0,
      opacity: 1,
      scale: 1,
      duration: 0.4,
      ease: 'expo.out',
    }
  )

  // Stagger Items
  tl.fromTo(
    '.lang-dropdown__item',
    { y: 5, opacity: 0 },
    {
      y: 0,
      opacity: 1,
      duration: 0.3,
      stagger: 0.05,
      ease: 'power2.out',
    },
    '-=0.3'
  )
}

const closeDropdown = () => {
  if (!dropdownRef.value) return

  gsap.to(dropdownRef.value, {
    y: 5,
    opacity: 0,
    scale: 0.98,
    duration: 0.2,
    ease: 'power2.in',
    onComplete: () => {
      isOpen.value = false
    },
  })
}

const handleItemClick = (index, event) => {
  createMagicSparks(event.clientX, event.clientY)
  selectLanguage(index)
}

const selectLanguage = (index) => {
  if (activeIndex.value === index) {
    closeDropdown()
    return
  }

  // Update logic to actually change the language
  const selectedLang = languages[index]
  locale.value = selectedLang.code.toLowerCase()

  // Persist preference (optional but recommended)
  localStorage.setItem('user-locale', locale.value)

  activeIndex.value = index
  closeDropdown()
}

// Spark Effect Logic
const createMagicSparks = (x, y) => {
  if (!sparkContainer) {
    sparkContainer = document.createElement('div')
    sparkContainer.className = 'fixed inset-0 pointer-events-none z-[9999]'
    sparkContainer.style.cssText =
      'position: fixed; top: 0; left: 0; right: 0; bottom: 0; pointer-events: none; z-index: 9999;'
    document.body.appendChild(sparkContainer)
  }

  const sparkCount = 12
  const gradient = 'linear-gradient(45deg, #7c3aed 0%, #ffffff 50%, #c4b5fd 100%)' // Purple focus
  const boxShadow = '0 0 6px #7c3aed, 0 0 12px rgba(124, 58, 237, 0.5)'

  for (let i = 0; i < sparkCount; i++) {
    const spark = document.createElement('div')
    // className approach sometimes fails if styles aren't loaded, using inline styles for reliability in overlay
    spark.className = 'magic-spark'
    spark.style.cssText = `
      position: absolute;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      pointer-events: none;
      background: ${gradient};
      box-shadow: ${boxShadow};
      left: ${x}px;
      top: ${y}px;
      transform: translate(-50%, -50%) scale(0);
      opacity: 0;
    `

    sparkContainer.appendChild(spark)

    const angle = (i / sparkCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.5
    const distance = 40 + Math.random() * 60
    const endX = Math.cos(angle) * distance
    const endY = Math.sin(angle) * distance

    const tl = gsap.timeline({
      onComplete: () => {
        if (spark.parentNode) spark.remove()
      },
    })

    tl.to(spark, {
      duration: 0.1,
      scale: 1,
      opacity: 1,
      ease: 'power2.out',
    })
      .to(
        spark,
        {
          duration: 0.5,
          x: endX,
          y: endY,
          scale: 0.2,
          rotation: Math.random() * 360,
          ease: 'power2.out',
        },
        0
      )
      .to(
        spark,
        {
          duration: 0.2,
          opacity: 0,
          ease: 'power2.in',
        },
        0.3
      )
  }
}

// Click Outside Logic
const handleClickOutside = (event) => {
  if (containerRef.value && !containerRef.value.contains(event.target)) {
    if (isOpen.value) closeDropdown()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)

  // Initialize from storage if exists, otherwise keep default
  const savedLocale = localStorage.getItem('user-locale')
  if (savedLocale) {
    const validLocales = ['en', 'es', 'pt']
    if (validLocales.includes(savedLocale)) {
      locale.value = savedLocale
    }
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  if (sparkContainer && sparkContainer.parentNode) {
    sparkContainer.remove()
  }
})
</script>

<style scoped>
.lang-switcher {
  position: relative;
  z-index: 50;
}

/* Button Styles */
.lang-switcher__button {
  position: relative;
  z-index: 20;
  @apply flex items-center gap-2 px-2 py-2;
  @apply border-2 border-border rounded-full;
  @apply text-base font-medium bg-transparent;
  @apply transition-all duration-300 cursor-pointer;
  @apply hover:bg-surface-elevated hover:border-electric-purple;
}

.lang-switcher__button--active {
  @apply border-electric-purple bg-surface-elevated;
  /* Glow effect */
  box-shadow: 0 0 15px rgba(124, 58, 237, 0.2);
}

.navbar__icon {
  @apply w-5 h-5 text-[var(--color-steel)];
}

.arrow-icon {
  @apply w-4 h-4 transition-transform duration-300 text-[var(--color-steel)];
}

.arrow-icon--rotated {
  transform: rotate(180deg);
  @apply text-electric-purple;
}

.lang-code {
  @apply font-bold text-sm tracking-wider text-[var(--color-ash)];
}

/* Dropdown Menu */
.lang-dropdown {
  position: absolute;
  bottom: calc(100% + 12px);
  top: auto;
  right: 0; /* Align right */
  width: 180px;
  z-index: 10;
  @apply bg-surface-elevated border border-border rounded-xl;
  @apply p-2 flex flex-col gap-1;
  /* Glassmorphism / Premium Blur */
  backdrop-filter: blur(12px);
  background: rgba(
    var(--color-surface-elevated-rgb),
    0.9
  ); /* Assuming variable exists or usage standard */
  /* Fallback */
  box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.5);
  overflow: hidden;
}

.lang-dropdown__item {
  @apply flex items-center justify-between px-3 py-2 rounded-lg;
  @apply text-sm text-text-secondary cursor-pointer;
  @apply transition-all duration-200;
  user-select: none;
}

.lang-dropdown__item:hover {
  @apply bg-white/5 text-text-primary;
}

.lang-dropdown__item--active {
  @apply bg-electric-purple text-[var(--color-text-primary)] shadow-lg;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.3);
}

.lang-dropdown__item--active:hover {
  @apply bg-electric-purple;
}

.item-label {
  font-weight: 500;
}

.item-code {
  @apply text-xs opacity-50 uppercase;
}

/* Spark Animation Global */
:global(.magic-spark::before) {
  content: '';
  @apply absolute -inset-0.5;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.8) 0%, transparent 70%);
  animation: sparkle 0.6s ease-out;
}

@keyframes sparkle {
  0% {
    transform: scale(0) rotate(0deg);
    opacity: 1;
  }
  50% {
    transform: scale(1.2) rotate(180deg);
    opacity: 0.8;
  }
  100% {
    transform: scale(0.3) rotate(360deg);
    opacity: 0;
  }
}
</style>
